import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useThemeStore } from '../../../store/ConfigStore';
import { useToast } from '@/components/Toast';
import { useStore } from '@/store'
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Pagination } from "@/components/Pagination";
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import type { InvitationReview } from '@/types/invitation';
import { fetchReviewList } from '@/services/invitation';
import { MobileTableCard } from '@/components/MobileCards';
import { validateNumericID } from '@/utils/validator';
import dayjs from 'dayjs';
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';

export const routeConfig: RouteConfig = {
    title: 'route.customerManagement.operationRecord',
    layout: true,
    sort: 2,
    hiddenInMenu: true
};

const InvitationReview = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore(); // 引入主题状态
    const toast = useToast();

    const [customerUID, setCustomerUID] = useState<string>('');
    const [type, setType] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [invitationReviewData, setInvitationReviewData] = useState<InvitationReview[]>([]);
    const [copiedUID, setCopiedUID] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    // For managing view details dialog
    // const [viewDetailsDialogVisible, setViewDetailsDialogVisible] = useState(false);
    // const [selectedLink, setSelectedLink] = useState<InvitationReview | null>(null);

    // Helper to build params for invitation API
    const buildInvitationParams = (customerUID: string, op_type?: number | null) => {
        let params: any = {};
        if (customerUID !== '') {
            params.user_id = customerUID;
        }
        if (op_type !== null && op_type !== 0) {
            params.op_type = op_type;
        }
        return params;
    };

    const handleReset = async () => {
        try {
            setCustomerUID('');
            setType(null);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            const res = await fetchReviewList({}, 1, pagination.pageSize);
            setInvitationReviewData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.review.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            // Validate customerUID before querying
            if (customerUID.trim() !== '') {
                const validation = validateNumericID(customerUID, 'UID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            const params = buildInvitationParams(customerUID, type);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchReviewList(params, 1, pagination.pageSize);
            setInvitationReviewData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.review.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    // const handleViewDetailsOpen = (record: InvitationReview) => {
    //     // Find the corresponding details
    //     const details = invitationReviewData.find(item => item.customerUID === record.customerUID);
    //     if (details) {
    //         // Clone to avoid direct mutation
    //         setSelectedLink(details);
    //         setViewDetailsDialogVisible(true);
    //     }
    // };

    // const handleViewDetailsDialogClose = () => {
    //     setViewDetailsDialogVisible(false);
    //     setSelectedLink(null);
    // };

    // const handleDrawerSave = () => {
    //     // Save logic here
    //     if (selectedLink) {
    //         // Update invitationReviewData with the new values
    //         setInvitationReviewData(prev => prev.map(item =>
    //             item.customerUID === selectedLink.customerUID ? { ...selectedLink } : item
    //         ));
    //     }
    //     setViewDetailsDialogVisible(false);
    //     setSelectedLink(null);
    // };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const handleCopyUID = async (uid: string) => {
        try {
            await navigator.clipboard.writeText(uid);
            setCopiedUID(uid);
            setTimeout(() => setCopiedUID(null), 2000);
        } catch (err) {
            // handle error
        }
    };

    const queryFields: QueryField[] = [
        {
            type: 'input',
            name: 'customerUID',
            label: t('invitationPromotion.review.customerUID'),
            value: customerUID,
            onChange: setCustomerUID,
            placeholder: t('invitationPromotion.review.pleaseEnterPlaceholder'),
        },
        {
            type: 'select',
            name: 'type',
            label: t('invitationPromotion.review.type'),
            value: type,
            onChange: setType,
            options: [
                // get from review 
                { value: null, label: t('invitationPromotion.review.all') },
                { value: 5, label: t('invitationPromotion.review.add') }, // Add Agent
                { value: 6, label: t('invitationPromotion.review.modify') }, // Modify Agent
                // { value: 12, label: t('invitationPromotion.review.topAgentAdd') }, // Add Top Agent
            ]
        }
    ];

    const invitationReviewColumns: ColumnsType<InvitationReview> = [
        {
            title: t('invitationPromotion.review.table.submissionTime'),
            dataIndex: 'submissionTime',
            key: 'submissionTime',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        {
            title: t('invitationPromotion.review.table.customerUID'),
            dataIndex: 'customerUID',
            key: 'customerUID',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1">
                    <span>{text === '0' ? t('customerManagement.superAgent.none') : text}</span>
                    { text !== '0' && (
                        <button
                            className="border-none bg-transparent p-0 m-0"
                            onClick={() => handleCopyUID(text)}
                        >
                            <img
                                src={copiedUID === text ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                                alt={copiedUID === text ? 'copied' : 'copy'}
                                className="w-4 h-4"
                            />
                        </button>
                    )}
                </div>
            ),
        },
        {
            title: t('invitationPromotion.review.table.type'),
            dataIndex: 'type',
            key: 'type',
            align: 'start',
            width: 110,
            ellipsis: true,
            render: (text: number) => {
                const labels: { [key: number]: string } = {
                    5: t('invitationPromotion.review.add'),
                    6: t('invitationPromotion.review.modify'),
                    8: t('invitationPromotion.review.groupUpsert'),
                    9: t('invitationPromotion.review.groupMemberUpsert'),
                    12: t('invitationPromotion.review.topAgentAdd'),
                };
                return <span>{labels[text] || text}</span>;
            },
        },
        {
            title: t('invitationPromotion.review.table.spot'),
            dataIndex: 'spot',
            key: 'spot',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => <span>{text}%</span>
        },
        {
            title: t('invitationPromotion.review.table.futures'),
            dataIndex: 'futures',
            key: 'futures',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => <span>{text}%</span>
        },
        // {
        //     title: t('invitationPromotion.review.table.alpha'),
        //     dataIndex: 'alpha',
        //     key: 'alpha',
        //     align: 'start',
        //     width: 130,
        //     ellipsis: true,
        //     render: (text) => <span>{text}%</span>
        // },
        {
            title: t('invitationPromotion.review.table.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'start',
            width: 100,
            ellipsis: true
        },
        {
            title: t('invitationPromotion.review.table.reason'),
            dataIndex: 'reason',
            key: 'reason',
            align: 'start',
            width: 150,
            ellipsis: true,
        },
        // {
        //     title: t('invitationPromotion.review.table.operation'),
        //     dataIndex: 'operation',
        //     key: 'operation',
        //     align: 'end',
        //     width: 110,
        //     fixed: 'right',
        //     render: (_, record) => (
        //         <div className="flex items-center justify-end gap-2">
        //             <button 
        //                 className="text-sm font-normal text-special-text font-inter w-14 bg-transparent border-none flex justify-center items-center"
        //                 onClick={() => handleViewDetailsOpen(record)}
        //             >
        //                 {t('invitationPromotion.review.table.viewDetails')}
        //             </button>
        //         </div>
        //     )
        // }
    ];

    const mobileInvitationReviewColumns = invitationReviewColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // try {
                //     const groupRes = await fetchGroupID({});
                //     setCustomerGroups(groupRes);
                // } catch (groupError: any) {
                //     toast.error({
                //         message: groupError?.message || t('customerManagement.clientList.groupFetchError') || 'An error occurred while fetching group list.'
                //     });
                // }

                try {
                    const params = buildInvitationParams(customerUID, type);
                    const res = await fetchReviewList(params, pagination.current, pagination.pageSize);
                    setInvitationReviewData(res.list);
                    setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('invitationPromotion.review.title')}
                subtitle={t('invitationPromotion.review.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                gridCols="grid-cols-2 md:grid-cols-4"
            />
            
            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={invitationReviewData}
                        columns={invitationReviewColumns}
                        rowKey="submissionTime"
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 850 }}
                        className="custom-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {invitationReviewData.map((row, idx) => (
                        <MobileTableCard
                            key={row.submissionTime}
                            record={row}
                            columns={mobileInvitationReviewColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default InvitationReview;
